import {
  TCampaignsResponse,
  TEntityResponse,
  TLocationListResponse,
  TCampaignsResultData,
  TEntityResultData,
  TLocationResultData,
  TServicesResponse,
  TServiceResultData,
  TEmployeeAgentListRes,
  TEmployeeAgentListData,
  TEmployeeSupervisorListDataSchema,
  TEmployeeSupervisorListResponseSchema,
  TEmployeeListBasedRankResponseSchema,
  TEmployeeListBasedRankResData,
} from "@/api/common/common.types";
import {
  TGetPostsTemplateResponsePayload,
  TManageTemplateObjData,
} from "@/api/manage-template";
import type {
  TAIEngineConfigEngine,
  TAIEngineOptionsResponse,
} from "@/api/ai-engine-config/ai-engine-config.types";

import { TSelectOption } from "@/components/forms/Select";
import { PAGE_LIMIT_TWENTY } from "@/core/const";
import { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";

/**
 * Generic API Response Structure
 * Assumes standard paginated API response format
 */
export type TGenericApiResponse<T> = {
  data?: {
    results?: T[];
    count?: number;
    current_page?: number;
    next?: string;
    previous?: string;
    total_pages?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

/**
 * Generic Query Hook Type
 * Accepts params and returns standard React Query response
 * Compatible with React Query's UseQueryResult type
 */
export type TQueryHook<TParams = any, TData = any, TError = any> = (
  params: TParams,
  options?: Partial<UseQueryOptions<TData, TError>>,
) => UseQueryResult<TData, TError>;

/**
 * Base configuration for all filter dropdowns with pagination
 */
export type TFilterDropdownConfig = {
  /** Unique identifier for this dropdown type */
  id: string;

  /** Display label (i18n key) */
  labelKey: string;

  /** Placeholder text (i18n key) */
  placeholderKey: string;

  /** Lazy-load the query hook function */
  loadQueryHook: () => Promise<TQueryHook<any, any>>;

  /** Transform raw data item to select option */
  transformToOption: (item: any) => TSelectOption;

  /** Page size for pagination */
  pageSize?: number;

  /** React Query options */
  queryOptions?: {
    retry?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnMount?: boolean;
  };

  /** Default params sent with every API request (merged below page/search params) */
  defaultParams?: Record<string, unknown>;

  /** When true, dropdown is searchable and API is called with search parameter on input */
  supportsSearch?: boolean;

  /** Option used to select every currently available item in a multi-select */
  selectAllOption?: TSelectOption;
};

export const SELECT_ALL_OPTION_VALUE = "__select_all__";

export const SELECT_ALL_OPTION: TSelectOption = {
  value: SELECT_ALL_OPTION_VALUE,
  label: "Select All",
};

/**
 * Registry of all filterable dropdown configurations
 */
export const filterDropdownRegistry: Record<
  TFilterDropdownType,
  TFilterDropdownConfig
> = {
  campaign: {
    id: "campaign",
    labelKey: "FILTER.CAMPAIGN",
    placeholderKey: "FILTER.SELECT_CAMPAIGN",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) => m.useGetCampaignDataQuery as TQueryHook<any, TCampaignsResponse>,
      ),
    transformToOption: (item: TCampaignsResultData) => ({
      value: item.internal_id,
      label: item.name,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  service: {
    id: "service",
    labelKey: "FILTER.SERVICE",
    placeholderKey: "FILTER.SELECT_SERVICE",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) => m.useGetServicesDataQuery as TQueryHook<any, TServicesResponse>,
      ),
    transformToOption: (item: TServiceResultData) => ({
      value: item.internal_id,
      label: item.name,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  entity: {
    id: "entity",
    labelKey: "FILTER.ENTITY",
    placeholderKey: "FILTER.SELECT_ENTITY",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) => m.useGetEntityDataQuery as TQueryHook<any, TEntityResponse>,
      ),
    transformToOption: (item: TEntityResultData) => ({
      value: item.internal_id,
      label: item.name,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  location: {
    id: "location",
    labelKey: "FILTER.LOCATION",
    placeholderKey: "FILTER.SELECT_LOCATION",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) =>
          m.useGetLocationListQuery as TQueryHook<any, TLocationListResponse>,
      ),
    transformToOption: (item: TLocationResultData) => ({
      value: item.internal_id,
      label: item.name,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  agent: {
    id: "agent",
    labelKey: "FILTER.AGENT",
    placeholderKey: "FILTER.SELECT_AGENT",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) =>
          m.useGetEmployeeAgentListQuery as TQueryHook<
            any,
            TEmployeeAgentListRes
          >,
      ),
    transformToOption: (item: TEmployeeAgentListData) => ({
      value: item.internal_id,
      label: `${item.first_name} ${item.last_name}`,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  template: {
    id: "template",
    labelKey: "FILTER.TEMPLATE",
    placeholderKey: "FILTER.SELECT_TEMPLATE",
    loadQueryHook: () =>
      import("@/api/manage-template").then(
        (m) =>
          m.useManageTemplateGetQuery as TQueryHook<
            any,
            TGetPostsTemplateResponsePayload
          >,
      ),
    transformToOption: (item: TManageTemplateObjData) => ({
      value: item.internal_id,
      label: item.name,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    defaultParams: {
      active: true,
    },
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  userSupervisor: {
    id: "userSupervisor",
    labelKey: "FILTER.QASUPERVISOR",
    placeholderKey: "FILTER.SELECT_QA_SUPERVISOR",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) =>
          m.useGetEmployeeSupervisorListQuery as TQueryHook<
            any,
            TEmployeeSupervisorListResponseSchema
          >,
      ),
    transformToOption: (item: TEmployeeSupervisorListDataSchema) => ({
      value: item.internal_id ?? "",
      label: `${item.first_name} ${item.last_name}`,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  employeeSupervisor: {
    id: "employeeSupervisor",
    labelKey: "FILTER.QASUPERVISOR",
    placeholderKey: "FILTER.SELECT_QA_SUPERVISOR",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) =>
          m.useGetEmployeeSupervisorListQuery as TQueryHook<
            any,
            TEmployeeSupervisorListResponseSchema
          >,
      ),
    transformToOption: (item: TEmployeeSupervisorListDataSchema) => ({
      value: item.internal_id ?? "",
      label: `${item.first_name} ${item.last_name}`,
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  aiEngine: {
    id: "aiEngine",
    labelKey: "FILTER.AI_ENGINE",
    placeholderKey: "FILTER.SELECT_AI_ENGINE",
    loadQueryHook: () =>
      import("@/api/ai-engine-config").then(
        (m) =>
          m.useGetAIEngineOptionsQuery as TQueryHook<
            any,
            TAIEngineOptionsResponse
          >,
      ),
    transformToOption: (item: TAIEngineConfigEngine) => ({
      value: item.name || item.slug || String(item.internal_id),
      label: item.name || item.slug || String(item.internal_id),
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  aiEngineConfig: {
    id: "aiEngineConfig",
    labelKey: "FILTER.AI_ENGINE",
    placeholderKey: "FILTER.SELECT_AI_ENGINE",
    loadQueryHook: () =>
      import("@/api/ai-engine-config").then(
        (m) =>
          m.useGetAIEngineOptionsQuery as TQueryHook<
            any,
            TAIEngineOptionsResponse
          >,
      ),
    transformToOption: (item: TAIEngineConfigEngine) => ({
      value: String(item.internal_id),
      label: item.name || item.slug || String(item.internal_id),
    }),
    pageSize: PAGE_LIMIT_TWENTY,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },

  rankBasedAuditor: {
    id: "rankBasedAuditor",
    labelKey: "FILTER.AUDITOR",
    placeholderKey: "FILTER.SELECT_AUDITOR",
    loadQueryHook: () =>
      import("@/api/common/common.hooks.lazy").then(
        (m) =>
          m.useGetEmployeeListBasedRankQuery as TQueryHook<
            any,
            TEmployeeListBasedRankResponseSchema
          >,
      ),
    transformToOption: (item: TEmployeeListBasedRankResData) => ({
      value: item.internal_id ?? "",
      label: `${item.first_name} ${item.last_name}`,
    }),
    pageSize: 5,
    queryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
    },
    supportsSearch: true,
    selectAllOption: SELECT_ALL_OPTION,
  },
  achievement: {
    id: "achievement",
    labelKey: "FILTER.ACHIEVEMENTS",
    placeholderKey: "FILTER>SELECTE_ACHIEVEMENT",
    loadQueryHook: () =>
      import("@/api/agent-performance/agent-performance.hooks").then(
        (m) => m.useGetAgentAchievementsQuery as TQueryHook<any, any>,
      ),
    transformToOption: (item: any) => {
      if (typeof item === "string") {
        return {
          value: item,
          label: item
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        };
      }
      return {
        value: item?.name ?? "",
        label: item?.title ?? "",
      };
    },
    queryOptions: { retry: false, refetchOnWindowFocus: false },
    supportsSearch: false,
  },
};

/**
 * Hook to get configuration for a filter dropdown type
 */
export const useFilterDropdownConfig = (dropdownType: string) => {
  const typedDropdownType = dropdownType as TFilterDropdownType;
  if (!typedDropdownType || !filterDropdownRegistry[typedDropdownType]) {
    return null;
  }

  return filterDropdownRegistry[typedDropdownType];
};

/**
 * Type helper to extract data type from config
 */
export type TFilterDropdownType =
  | "campaign"
  | "service"
  | "entity"
  | "location"
  | "agent"
  | "template"
  | "userSupervisor"
  | "employeeSupervisor"
  | "rankBasedAuditor"
  | "achievement"
  | "aiEngine"
  | "aiEngineConfig";
